import type { BookingDetailData, BookingDetailPickup, LookupDetailItem } from "api-client";
import {
  resolveEquipmentPiecesSlugFromWire,
  resolveExpiresWhenSlugFromWire,
} from "api-client";
import type {
  CreateBookingDropoffStackEquipmentValues,
  CreateBookingDropoffWheeledEquipmentValues,
  CreateBookingEquipmentPieces,
  CreateBookingExpiresWhen,
  CreateBookingPickupEquipmentValues,
  CreateBookingReeferChoice,
  CreateBookingScheduleStepValues,
  CreateBookingStepOneValues,
  CreateBookingStorageType,
} from "design-components";
import { parseBookingWireDateTimeWallClock, shouldPrefillScheduleDateTime, startOfCalendarDay, timeAnchorFromWallClock } from "design-components";
import {
  buildPickupDetailEquipmentOptionId,
  buildPickupMultiPieceWireRowOptionId,
  findPickupEquipmentIdsFromMultiPieceDetails,
  findPickupEquipmentOptionIdFromMultiPieceRow,
  findPickupStaticEquipmentFromPickupBranch,
  formatPickupMultiPieceWireRowLabel,
  isPickupMultiPieceWireRowOptionId,
  type PickupMultiPieceEquipmentWireRow,
} from "../constants/pickup-static-equipment.constants";

export interface EditBookingLoadedFees {
  baseSurchargeFee: number;
  afterHour: number;
  weekendMultiplier: number;
  totalCostEstimate: number;
}

export interface EditBookingWizardHydration {
  bookingId: number;
  stepOne: CreateBookingStepOneValues;
  schedule: CreateBookingScheduleStepValues;
  pickupEquipment: CreateBookingPickupEquipmentValues | null;
  dropoffStack: CreateBookingDropoffStackEquipmentValues | null;
  dropoffWheeled: CreateBookingDropoffWheeledEquipmentValues | null;
  /** GET .../bookings/{bookingId} `sealCapture.sealCaptureEnabled` (FR-AV186-003). */
  sealCaptureEnabled: boolean;
  /** Day rate from GET .../bookings/{bookingId} `dayRate` (AV-131). */
  prefillDayRate: number | null;
  loadedFees: EditBookingLoadedFees;
  pickupEquipmentDetails: string | null;
  pickupDetailSnapshot: BookingDetailPickup | null;
}

export interface EditBookingHydrationLookups {
  equipmentPiecesLookup?: LookupDetailItem[];
  expiresWhenLookup?: LookupDetailItem[];
}

export function mapBookingDetailToEditWizard(
  detail: BookingDetailData,
  lookups: EditBookingHydrationLookups = {},
): EditBookingWizardHydration {
  const equipmentPieces = resolveEditEquipmentPieces(detail, lookups.equipmentPiecesLookup ?? []);
  const storageType = resolveStepOneStorageType(detail);
  return {
    bookingId: detail.bookingId,
    stepOne: {
      customerId: String(detail.customerId),
      locationId: String(detail.locationId),
      bookingType: detail.bookingType,
      storageType,
      equipmentPieces,
    },
    schedule: mapEditSchedulePrefill(detail),
    pickupEquipment: detail.bookingType === "pickup" ? mapPickupEquipment(detail, lookups) : null,
    dropoffStack:
      detail.bookingType === "dropoff" && storageType === "stack"
        ? mapDropoffStack(detail, lookups)
        : null,
    dropoffWheeled:
      detail.bookingType === "dropoff" && storageType === "wheeled" ? mapDropoffWheeled(detail) : null,
    sealCaptureEnabled: mapSealFields(detail.sealCapture).requireSealCapture,
    prefillDayRate: resolveEditPrefillDayRate(detail),
    loadedFees: {
      baseSurchargeFee: detail.baseSurchargeFee,
      afterHour: detail.afterHour,
      weekendMultiplier: detail.weekendMultiplier,
      totalCostEstimate: detail.totalCostEstimate,
    },
    pickupEquipmentDetails: detail.pickup?.equipmentDetails?.trim() || null,
    pickupDetailSnapshot: detail.pickup,
  };
}

function resolveEditPrefillDayRate(detail: BookingDetailData): number | null {
  const { dayRate } = detail;
  if (dayRate == null || !Number.isFinite(dayRate) || dayRate < 0) {
    return null;
  }
  return dayRate;
}

function resolveEditEquipmentPieces(
  detail: BookingDetailData,
  equipmentPiecesLookup: LookupDetailItem[],
): CreateBookingEquipmentPieces | null {
  const fromWire = resolveEquipmentPiecesSlugFromWire(detail.equipmentPieces, equipmentPiecesLookup);
  if (fromWire) return fromWire;
  if (detail.bookingType === "pickup") {
    const multipieceRows = detail.pickup?.multiPieceEquipmentDetails?.length ?? 0;
    const stackQuantity = detail.pickup?.stack?.containerQuantity;
    if (multipieceRows > 0 || (stackQuantity != null && stackQuantity > 1)) {
      return "multiple_pieces";
    }
    return "one_piece";
  }
  if (detail.bookingType === "dropoff" && detail.dropoff?.storageType === "stack") {
    const stackQuantity = detail.dropoff.stack?.containerQuantity;
    if (stackQuantity != null && stackQuantity > 1) {
      return "multiple_pieces";
    }
    return "one_piece";
  }
  return null;
}

function mapEditSchedulePrefill(detail: BookingDetailData): CreateBookingScheduleStepValues {
  const scheduled = mapWireScheduleToStepParts(detail.scheduledDateTime);
  const mustArriveRaw =
    detail.mustArriveByDateTime.trim() ||
    composeArrivalScheduleIso(detail.arrivalDate, detail.arrivalTime);
  const mustArrive = mapWireScheduleToStepParts(mustArriveRaw);

  return {
    scheduledDateTime: scheduled.combined,
    mustArriveBy: mustArrive.combined,
    scheduledDatePart: scheduled.datePart,
    scheduledTimePart: scheduled.timePart,
    mustArriveDatePart: mustArrive.datePart,
    mustArriveTimePart: mustArrive.timePart,
  };
}

function mapWireScheduleToStepParts(wire: string): {
  datePart: Date | null;
  timePart: Date | null;
  combined: Date | null;
} {
  const trimmed = wire.trim();
  if (!trimmed) {
    return { datePart: null, timePart: null, combined: null };
  }

  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  const parsed = parseBookingDateTimeIso(trimmed);
  if (!parsed || !shouldPrefillScheduleDateTime(parsed, new Date(), { dateOnly: isDateOnly })) {
    return { datePart: null, timePart: null, combined: null };
  }

  if (isDateOnly) {
    return {
      datePart: startOfCalendarDay(parsed),
      timePart: null,
      combined: null,
    };
  }

  return {
    datePart: startOfCalendarDay(parsed),
    timePart: timeAnchorFromWallClock(parsed),
    combined: parsed,
  };
}

function composeArrivalScheduleIso(arrivalDate: string, arrivalTime: string): string {
  const parsed = parseArrivalSchedule(arrivalDate, arrivalTime);
  if (!parsed) return "";
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  const hh = String(parsed.getHours()).padStart(2, "0");
  const mm = String(parsed.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d}T${hh}:${mm}:00`;
}

function resolveStepOneStorageType(detail: BookingDetailData): CreateBookingStorageType | null {
  if (detail.bookingType === "pickup") {
    const pickupStorage = detail.pickup?.storageType;
    if (pickupStorage === "stack" || pickupStorage === "wheeled") return pickupStorage;
    return null;
  }
  const dropoffStorage = detail.dropoff?.storageType;
  if (dropoffStorage === "stack" || dropoffStorage === "wheeled") return dropoffStorage;
  return "stack";
}

function mapPickupEquipment(
  detail: BookingDetailData,
  lookups: EditBookingHydrationLookups,
): CreateBookingPickupEquipmentValues {
  const seal = mapSealFields(detail.sealCapture);
  const equipmentPieces = resolveEditEquipmentPieces(detail, lookups.equipmentPiecesLookup ?? []);
  const expiresWhenLookup = lookups.expiresWhenLookup ?? [];

  if (equipmentPieces === "multiple_pieces") {
    const multiPieceRows = detail.pickup?.multiPieceEquipmentDetails ?? [];
    const equipmentIds = resolvePickupMultipieceEquipmentIds(multiPieceRows);
    const stack = detail.pickup?.stack;
    return {
      equipmentId: null,
      equipmentIds,
      quantity: stack?.containerQuantity ?? null,
      expiresWhen: resolveExpiresWhenFromWire(stack?.expiresWhen, expiresWhenLookup),
      requireSealCapture: seal.requireSealCapture,
    };
  }

  const matched = findPickupStaticEquipmentFromPickupBranch(detail.pickup);
  const equipmentDetails = detail.pickup?.equipmentDetails?.trim() ?? "";
  return {
    equipmentId:
      matched?.optionId ??
      (equipmentDetails ? buildPickupDetailEquipmentOptionId(detail.bookingId) : null),
    equipmentIds: [],
    quantity: null,
    expiresWhen: null,
    requireSealCapture: seal.requireSealCapture,
  };
}

function resolvePickupMultipieceEquipmentIds(
  multiPieceRows: NonNullable<BookingDetailPickup["multiPieceEquipmentDetails"]>,
): string[] {
  const catalogIds = findPickupEquipmentIdsFromMultiPieceDetails(multiPieceRows);
  if (catalogIds.length === multiPieceRows.length) {
    return catalogIds;
  }

  return multiPieceRows.map((row: PickupMultiPieceEquipmentWireRow, index: number) => {
    const catalogId = findPickupEquipmentOptionIdFromMultiPieceRow(row);
    if (catalogId) return catalogId;
    return buildPickupMultiPieceWireRowOptionId(index);
  });
}

function mapDropoffStack(
  detail: BookingDetailData,
  lookups: EditBookingHydrationLookups,
): CreateBookingDropoffStackEquipmentValues {
  const stack = detail.dropoff?.stack;
  const seal = mapSealFields(detail.sealCapture);
  const equipmentPieces = resolveEditEquipmentPieces(detail, lookups.equipmentPiecesLookup ?? []);
  const isMultiple = equipmentPieces === "multiple_pieces";

  return {
    isReefer: boolToReeferChoice(stack?.isContainerReefer),
    containerNumber: stack?.containerNumber ?? "",
    containerSize: stack?.containerSize ?? "",
    equipmentOwner: stack?.equipmentOwner ?? "",
    quantity: isMultiple ? (stack?.containerQuantity ?? null) : null,
    expiresWhen: isMultiple
      ? resolveExpiresWhenFromWire(stack?.expiresWhen, lookups.expiresWhenLookup ?? [])
      : null,
    requireSealCapture: seal.requireSealCapture,
  };
}

function resolveExpiresWhenFromWire(
  wireValue: string | null | undefined,
  expiresWhenLookup: LookupDetailItem[],
): CreateBookingExpiresWhen | null {
  return resolveExpiresWhenSlugFromWire(wireValue, expiresWhenLookup);
}

function mapDropoffWheeled(detail: BookingDetailData): CreateBookingDropoffWheeledEquipmentValues {
  const wheeled = detail.dropoff?.wheeled;
  const seal = mapSealFields(detail.sealCapture);
  return {
    droppedOffEquipment: mapWheeledVariantToUi(wheeled?.equipmentVariant),
    chassisId: wheeled?.chassis?.chassisId ?? "",
    licensePlate: wheeled?.chassis?.licensePlate ?? "",
    equipmentOwner: wheeled?.chassis?.equipmentOwner ?? "",
    containerIsReefer: boolToReeferChoice(wheeled?.container?.isReefer),
    containerNumber: wheeled?.container?.containerNumber ?? "",
    containerSize: wheeled?.container?.containerSize ?? "",
    containerEquipmentOwner: wheeled?.container?.equipmentOwner ?? "",
    trailerIsReefer: boolToReeferChoice(wheeled?.trailer?.isReefer),
    trailerNumber: wheeled?.trailer?.trailerNumber ?? "",
    trailerSize: wheeled?.trailer?.trailerSize ?? "",
    trailerLicensePlate: wheeled?.trailer?.licensePlate ?? "",
    trailerEquipmentOwner: wheeled?.trailer?.equipmentOwner ?? "",
    requireSealCapture: seal.requireSealCapture,
  };
}

function mapSealFields(seal: BookingDetailData["sealCapture"]): { requireSealCapture: boolean } {
  return { requireSealCapture: Boolean(seal.sealCaptureEnabled) };
}

function mapWheeledVariantToUi(
  variant: "chassis" | "chassisAndContainer" | "trailer" | undefined,
): CreateBookingDropoffWheeledEquipmentValues["droppedOffEquipment"] {
  if (!variant) return null;
  if (variant === "chassisAndContainer") return "chassis_and_container";
  if (variant === "trailer") return "trailer";
  return "chassis";
}

function boolToReeferChoice(value: boolean | undefined): CreateBookingReeferChoice | null {
  if (value === true) return "yes";
  if (value === false) return "no";
  return null;
}

function parseBookingDateTimeIso(iso: string): Date | null {
  return parseBookingWireDateTimeWallClock(iso);
}

/** Parse API `MM/DD/YYYY` + `h:mm AM/PM` into a Date for schedule pre-fill. */
export function parseArrivalSchedule(arrivalDate: string, arrivalTime: string): Date | null {
  const dateMatch = arrivalDate.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const timeMatch = arrivalTime.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!dateMatch || !timeMatch) return null;

  const month = parseInt(dateMatch[1], 10);
  const day = parseInt(dateMatch[2], 10);
  const year = parseInt(dateMatch[3], 10);
  let hour = parseInt(timeMatch[1], 10);
  const minute = parseInt(timeMatch[2], 10);
  const ampm = timeMatch[3].toUpperCase();

  if (ampm === "PM" && hour < 12) hour += 12;
  if (ampm === "AM" && hour === 12) hour = 0;

  const parsed = new Date(year, month - 1, day, hour, minute, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export { isPickupMultiPieceWireRowOptionId, formatPickupMultiPieceWireRowLabel };
